import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  clienteCreatePayload,
  maskCNPJ,
  maskPhoneCedente,
  validateClienteForm,
} from "../lib/cedenteOperations";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { razao_social: "", cnpj: "", contato_telefone: "", contato_email: "" };

/**
 * Dialog de cadastro INLINE de cliente/sacado (Q5 do owner, 2026-07-31).
 *
 * Padrão idêntico ao `RepresentativeFormDialog` — a única outra superfície
 * inline do wizard: dialog → POST → o chamador recarrega a lista e seleciona o
 * recém-criado na linha que originou o clique (`onCreated`).
 *
 * - SÓ criação (não edita; a edição vive na página de Clientes).
 * - Validação client-side é ESPELHO do `CedenteCreateClientePayload`; o
 *   servidor continua sendo a autoridade (409 de CNPJ duplicado é exibido
 *   com fidelidade, nunca sucesso silencioso).
 * - A11y: `htmlFor` em todos os labels, foco no primeiro campo ao abrir e
 *   devolução do foco ao gatilho quando fecha (`onCloseAutoFocus` +
 *   `returnFocusTo`).
 */
export default function ClienteFormDialog({ open, onOpenChange, onCreated, returnFocusTo }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setErrors({});
    setApiError(null);
  }, [open]);

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  };

  const submit = async (e) => {
    e.preventDefault();
    const localErrors = validateClienteForm(form);
    setErrors(localErrors);
    if (Object.values(localErrors).some(Boolean)) return;

    setSubmitting(true);
    setApiError(null);
    try {
      const { data } = await api.post("/cedente/clientes", clienteCreatePayload(form));
      toast.success("Cliente cadastrado");
      onOpenChange(false);
      // O chamador recarrega a lista e seleciona o recém-criado na linha certa.
      await onCreated?.(data);
    } catch (err) {
      // Erro do servidor exibido com fidelidade — ex.: 409 "Já existe um
      // cliente com este CNPJ". Nunca fechar o dialog em silêncio.
      setApiError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="cliente-dialog"
        onOpenAutoFocus={(e) => {
          // Foco explícito no primeiro campo (o default do Radix seria o
          // primeiro focável, que pode ser o botão de fechar).
          e.preventDefault();
          firstFieldRef.current?.focus();
        }}
        onCloseAutoFocus={(e) => {
          // Devolve o foco ao gatilho que abriu (o Select da linha), não ao
          // <body> — quem usa teclado continua de onde parou.
          if (returnFocusTo) {
            e.preventDefault();
            document.getElementById(returnFocusTo)?.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Cadastrar cliente (sacado)</DialogTitle>
          <DialogDescription>
            O cliente cadastrado aqui já fica selecionado como sacado deste recebível.
            Você não perde o que já preencheu.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cliente-razao-social">Razão social *</Label>
            <Input
              id="cliente-razao-social"
              ref={firstFieldRef}
              required
              value={form.razao_social}
              onChange={(e) => set("razao_social", e.target.value)}
              placeholder="Empresa XYZ Ltda"
              aria-invalid={!!errors.razao_social}
              data-testid="cliente-razao-social"
            />
            {errors.razao_social && <p className="text-[12px] text-red-600" role="alert">{errors.razao_social}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cliente-cnpj">CNPJ *</Label>
            <Input
              id="cliente-cnpj"
              required
              value={form.cnpj}
              onChange={(e) => set("cnpj", maskCNPJ(e.target.value))}
              placeholder="00.000.000/0000-00"
              maxLength={18}
              className="font-mono-num"
              aria-invalid={!!errors.cnpj}
              data-testid="cliente-cnpj"
            />
            {errors.cnpj && <p className="text-[12px] text-red-600" role="alert">{errors.cnpj}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cliente-telefone">Telefone</Label>
              <Input
                id="cliente-telefone"
                value={form.contato_telefone}
                onChange={(e) => set("contato_telefone", maskPhoneCedente(e.target.value))}
                placeholder="+55 11 99999-9999"
                className="font-mono-num"
                data-testid="cliente-telefone"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cliente-email">E-mail</Label>
              <Input
                id="cliente-email"
                type="text"
                value={form.contato_email}
                onChange={(e) => set("contato_email", e.target.value)}
                placeholder="contato@cliente.com.br"
                aria-invalid={!!errors.contato_email}
                data-testid="cliente-email"
              />
              {errors.contato_email && <p className="text-[12px] text-red-600" role="alert">{errors.contato_email}</p>}
            </div>
          </div>

          <p className="text-[11px] text-zinc-400">
            Razão social e CNPJ são obrigatórios. Telefone e e-mail podem ser
            completados depois, em <strong>Clientes</strong>.
          </p>

          {apiError && (
            <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800" data-testid="cliente-api-error">
              {apiError}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={submitting} data-testid="cliente-submit">
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : <Plus className="h-4 w-4 mr-2" />}
              Cadastrar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
